import { supabase } from '../supabase';

// Define types for Google Tasks API responses
interface GoogleTaskList {
  id: string;
  title: string;
  updated: string;
  selfLink: string;
}

interface GoogleTask {
  id: string;
  title: string;
  notes?: string;
  due?: string;
  completed?: string;
  status: 'needsAction' | 'completed';
  updated: string;
}

interface GoogleTasksCredentials {
  access_token: string;
  refresh_token?: string;
  expiry_date?: number;
  taskLists?: GoogleTaskList[];
  lastSync?: string;
}

/**
 * Authenticate with Google Tasks API
 * This function initiates the OAuth flow for Google Tasks
 */
export async function authenticateGoogleTasks() {
  try {
    // Use the client ID from environment variables
    const clientId = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID;
    if (!clientId) {
      throw new Error('Google OAuth client ID not configured');
    }

    // Define the redirect URI
    const redirectUri = import.meta.env.VITE_GOOGLE_OAUTH_REDIRECT_URI || `${window.location.origin}/dashboard/settings`;
    
    // Define the scopes needed for Google Tasks
    const scopes = [
      'https://www.googleapis.com/auth/tasks',
      'https://www.googleapis.com/auth/tasks.readonly'
    ];

    // Build the authorization URL - Fixed to use code response type instead of token
    const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
    authUrl.searchParams.append('client_id', clientId);
    authUrl.searchParams.append('redirect_uri', redirectUri);
    authUrl.searchParams.append('response_type', 'code'); // Changed from 'token' to 'code'
    authUrl.searchParams.append('scope', scopes.join(' '));
    authUrl.searchParams.append('access_type', 'offline');
    authUrl.searchParams.append('prompt', 'consent');
    authUrl.searchParams.append('include_granted_scopes', 'true');
    
    // Redirect to the authorization URL
    window.location.href = authUrl.toString();
    
    return { success: true };
  } catch (error) {
    console.error('Google Tasks authentication error:', error);
    throw error;
  }
}

/**
 * Exchange authorization code for access token
 */
export async function exchangeCodeForToken(code: string, redirectUri: string): Promise<GoogleTasksCredentials> {
  try {
    const clientId = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID;
    const clientSecret = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_SECRET;
    
    if (!clientId || !clientSecret) {
      throw new Error('Google OAuth credentials not configured');
    }
    
    // Call the token endpoint
    const response = await fetch('https://oauth2.googleapis.com/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        code,
        client_id: clientId,
        client_secret: clientSecret,
        redirect_uri: redirectUri,
        grant_type: 'authorization_code',
      }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Failed to exchange code: ${errorData.error_description || response.statusText}`);
    }
    
    const data = await response.json();
    
    return {
      access_token: data.access_token,
      refresh_token: data.refresh_token,
      expiry_date: Date.now() + (data.expires_in * 1000),
    };
  } catch (error) {
    console.error('Error exchanging code for token:', error);
    throw error;
  }
}

/**
 * Save Google Tasks credentials to the database
 */
export async function saveGoogleTasksCredentials(credentials: GoogleTasksCredentials) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('user_credentials')
      .upsert({
        user_id: user.id,
        provider: 'google_tasks',
        credentials: {
          access_token: credentials.access_token,
          refresh_token: credentials.refresh_token,
          expiry_date: credentials.expiry_date,
          taskLists: credentials.taskLists,
          lastSync: new Date().toISOString()
        }
      })
      .select();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error saving Google Tasks credentials:', error);
    throw error;
  }
}

/**
 * Get Google Tasks credentials from the database
 */
export async function getGoogleTasksCredentials(): Promise<GoogleTasksCredentials | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('user_credentials')
      .select('credentials')
      .eq('user_id', user.id)
      .eq('provider', 'google_tasks')
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // No data found
      throw error;
    }

    return data.credentials as GoogleTasksCredentials;
  } catch (error) {
    console.error('Error getting Google Tasks credentials:', error);
    throw error;
  }
}

/**
 * Fetch task lists from Google Tasks API
 */
export async function fetchGoogleTaskLists(accessToken: string): Promise<GoogleTaskList[]> {
  try {
    const response = await fetch('https://tasks.googleapis.com/tasks/v1/users/@me/lists', {
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch task lists: ${response.statusText}`);
    }

    const data = await response.json();
    return data.items || [];
  } catch (error) {
    console.error('Error fetching Google Task lists:', error);
    throw error;
  }
}

/**
 * Fetch tasks from a specific task list
 */
export async function fetchGoogleTasks(accessToken: string, taskListId: string): Promise<GoogleTask[]> {
  try {
    const response = await fetch(`https://tasks.googleapis.com/tasks/v1/lists/${taskListId}/tasks`, {
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch tasks: ${response.statusText}`);
    }

    const data = await response.json();
    return data.items || [];
  } catch (error) {
    console.error('Error fetching Google Tasks:', error);
    throw error;
  }
}

/**
 * Create a new task in Google Tasks
 */
export async function createGoogleTask(
  accessToken: string, 
  taskListId: string, 
  task: { title: string; notes?: string; due?: string }
): Promise<GoogleTask> {
  try {
    const response = await fetch(`https://tasks.googleapis.com/tasks/v1/lists/${taskListId}/tasks`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(task)
    });

    if (!response.ok) {
      throw new Error(`Failed to create task: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating Google Task:', error);
    throw error;
  }
}

/**
 * Update an existing task in Google Tasks
 */
export async function updateGoogleTask(
  accessToken: string,
  taskListId: string,
  taskId: string,
  updates: Partial<GoogleTask>
): Promise<GoogleTask> {
  try {
    const response = await fetch(`https://tasks.googleapis.com/tasks/v1/lists/${taskListId}/tasks/${taskId}`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(updates)
    });

    if (!response.ok) {
      throw new Error(`Failed to update task: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating Google Task:', error);
    throw error;
  }
}

/**
 * Delete a task from Google Tasks
 */
export async function deleteGoogleTask(
  accessToken: string,
  taskListId: string,
  taskId: string
): Promise<void> {
  try {
    const response = await fetch(`https://tasks.googleapis.com/tasks/v1/lists/${taskListId}/tasks/${taskId}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to delete task: ${response.statusText}`);
    }
  } catch (error) {
    console.error('Error deleting Google Task:', error);
    throw error;
  }
}

/**
 * Sync tasks between AdvisorGrow and Google Tasks
 */
export async function syncGoogleTasks(accessToken: string, taskListId: string): Promise<void> {
  try {
    // Fetch tasks from Google Tasks
    const googleTasks = await fetchGoogleTasks(accessToken, taskListId);
    
    // Fetch tasks from AdvisorGrow database
    const { data: localTasks, error } = await supabase
      .from('tasks')
      .select('*');
    
    if (error) throw error;
    
    // TODO: Implement sync logic based on last updated timestamp
    // This would compare tasks from both sources and sync accordingly
    
    // Update last sync time
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');
    
    await supabase
      .from('user_credentials')
      .update({
        credentials: {
          access_token: accessToken,
          lastSync: new Date().toISOString()
        }
      })
      .eq('user_id', user.id)
      .eq('provider', 'google_tasks');
      
  } catch (error) {
    console.error('Error syncing Google Tasks:', error);
    throw error;
  }
}

/**
 * Check if the user has authorized Google Tasks
 */
export async function isGoogleTasksAuthorized(): Promise<boolean> {
  try {
    const credentials = await getGoogleTasksCredentials();
    return !!credentials?.access_token;
  } catch (error) {
    console.error('Error checking Google Tasks authorization:', error);
    return false;
  }
}

/**
 * Disconnect Google Tasks integration
 */
export async function disconnectGoogleTasks(): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { error } = await supabase
      .from('user_credentials')
      .delete()
      .eq('user_id', user.id)
      .eq('provider', 'google_tasks');

    if (error) throw error;
  } catch (error) {
    console.error('Error disconnecting Google Tasks:', error);
    throw error;
  }
}

/**
 * Handle the OAuth callback
 */
export async function handleGoogleTasksCallback(url: string): Promise<void> {
  try {
    // Parse the URL to get the authorization code
    const urlObj = new URL(url);
    const code = urlObj.searchParams.get('code');
    
    if (!code) {
      throw new Error('No authorization code found in callback URL');
    }
    
    // Exchange the code for tokens
    const redirectUri = import.meta.env.VITE_GOOGLE_OAUTH_REDIRECT_URI || `${window.location.origin}/dashboard/settings`;
    const credentials = await exchangeCodeForToken(code, redirectUri);
    
    // Get task lists
    const taskLists = await fetchGoogleTaskLists(credentials.access_token);
    
    // Save credentials and task lists
    await saveGoogleTasksCredentials({
      ...credentials,
      taskLists
    });
    
    // Clear the URL parameters
    window.history.replaceState({}, document.title, window.location.pathname);
  } catch (error) {
    console.error('Error handling Google Tasks callback:', error);
    throw error;
  }
}